import React from 'react';
import { Button, Creatable, TextInput } from 'ui-toolkit';
import generateTitleFeesOptions from '../utilities/generateTitleFeesOptions';
import orderStatusIsNotLateStageOrArchived from '../utilities/orderStatusIsNotLateStageOrArchived';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import RequiredSvg from '../../../ui-toolkit/images/required.svg?react';
import { CdLabelCell } from '../CdLabelCell.styles';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasRefiDiscountEventProps from './IHasRefiDiscountEventProps';
import IItemProps from './IItemProps';
import SharedItemCells from './SharedItemCells';
import './_TitleFee.scss';
import { useApplicationContext } from '../context/useApplicationContext';
import { ITitleFeeOtherViewItem, ITitleFeeViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { generateDefaultViewItem } from '../../../ui-toolkit/cdItems/data/generateDefaultViewItem';
import { eUSAState } from '../../../ui-toolkit/enums/states';
import { TitleFeesThatGoInSectionH } from '../../../ui-toolkit/enums/TitleFeesThatGoInSectionH';
import { eTitleFees } from '../../../ui-toolkit/enums/eTitleFees';
import { eCDV3ItemTypes } from '../../../ui-toolkit/enums/eCDV3ItemTypes';

interface ITitleFee
  extends IItemProps,
    IHasItemDataProps<ITitleFeeViewItem | ITitleFeeOtherViewItem>,
    Required<IHasItemEventProps<ITitleFeeViewItem | ITitleFeeOtherViewItem>>,
    IHasRefiDiscountEventProps {}

export const TitleFee: React.FC<ITitleFee> = (props) => {
  const [{ order }] = useApplicationContext();

  const { data, onDetailsClick, onEligibilityCheck, onChange, hasEditPermissions } = props;
  const state = order ? order.property.state : undefined;
  const titleFeesAsOptions = generateTitleFeesOptions(state as eUSAState | undefined);
  const titleFeesThatGoInSectionHOptions = titleFeesAsOptions.filter((option) =>
    TitleFeesThatGoInSectionH.includes(option.value)
  );
  const selectedValue =
    data.type === eCDV3ItemTypes.title_fee
      ? titleFeesAsOptions.find((option) => option.value === (data as ITitleFeeViewItem).sub_type)
      : !!(data as ITitleFeeOtherViewItem).label
        ? {
            label: `Title - ${(data as ITitleFeeOtherViewItem).label}`,
            value: (data as ITitleFeeOtherViewItem).label,
          }
        : null;
  const orderStatus = (data as ITitleFeeViewItem).order_status;
  const refiDiscount = (data as ITitleFeeViewItem).refi_discount;
  const orderIsNotOld = orderStatusIsNotLateStageOrArchived(orderStatus);
  const compareOption = (inputValue: string, option: { label: string }) =>
    option.label.toLowerCase() === inputValue.toLowerCase();
  const isValidNewOption = (inputValue: string, selectValue: any, selectOptions: ReadonlyArray<any>) => {
    return !(!inputValue || selectOptions.some((option) => compareOption(inputValue, option)));
  };
  const goesInSectionH =
    !!selectedValue &&
    selectedValue.value != null &&
    TitleFeesThatGoInSectionH.map((option) => option.toString()).includes(selectedValue.value);
  const isSubtypeLocked = (!data.new || goesInSectionH) && !!selectedValue;

  const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
  const payeeOnChange = generateDefaultOnChangeHandler('payee');
  const onOptionSelected = (
    option?: {
      label: string;
      value: eTitleFees | string;
      __isNew__?: boolean;
    } | null
  ) => {
    const resetSelections = {
      label: undefined,
      sub_type: undefined,
      overridden: false,
    };
    if (option && option.value) {
      if (option.__isNew__) {
        // FYI: always set to title_fee_other because string option means custom/other
        onChange({
          ...generateDefaultViewItem(),
          ...data,
          ...resetSelections,
          dirty: true,
          label: option.value,
          type: eCDV3ItemTypes.title_fee_other,
        });
      } else {
        // FYI: always set to title_fee because option as object means enum/sub_type
        onChange({
          ...generateDefaultViewItem(),
          ...resetSelections,
          ...data,
          dirty: true,
          sub_type: option.value as eTitleFees,
          type: eCDV3ItemTypes.title_fee,
        });
      }
    } else {
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        ...resetSelections,
        dirty: true,
        type: eCDV3ItemTypes.title_fee,
      });
    }
  };

  const noOptionsMessage = (value: string) => {
    return `Create "Title - ${value}"`;
  };

  return (
    <SharedItemCells
      {...props}
      readOnly={!hasEditPermissions}
      extendedContent={
        refiDiscount &&
        ['EligibleByBulkQualifiedLender', 'EligibleByState', 'ConditionallyEligibleByState'].includes(
          refiDiscount.eligibility
        ) ? (
          !!refiDiscount.finalized_at ? (
            <div className="title-fee__refi-discount">
              <div className="title-fee__refi-discount__icon">
                <RequiredSvg />
              </div>
              <div className="title-fee__refi-discount__discount-status">
                {refiDiscount.eligibility === 'ConditionallyEligibleByState'
                  ? refiDiscount.applied
                    ? 'Discount Applied'
                    : 'Not eligible for discount'
                  : 'Discount Applied'}
              </div>
              <Button className="title-fee__refi-discount__details" onClick={onDetailsClick} intent="ghost" isInline>
                Details
              </Button>
            </div>
          ) : orderIsNotOld &&
            refiDiscount.eligibility === 'ConditionallyEligibleByState' &&
            !refiDiscount.finalized_at ? (
            <div className="title-fee__refi-discount">
              <div className="title-fee__refi-discount__icon">
                <RequiredSvg />
              </div>
              <Button
                className="title-fee__refi-discount__check-eligibility"
                onClick={onEligibilityCheck}
                intent="ghost"
                isInline
              >
                Check discount eligibility
              </Button>
            </div>
          ) : null
        ) : null
      }
    >
      <CdLabelCell gridTemplateColumns={isSubtypeLocked ? undefined : '1fr min-content 1fr'}>
        {isSubtypeLocked ? (
          <span>{`${selectedValue!.label}`}</span>
        ) : (
          <Creatable
            isDisabled={goesInSectionH || !hasEditPermissions}
            isValidNewOption={isValidNewOption}
            onChange={onOptionSelected}
            options={
              goesInSectionH
                ? [titleFeesThatGoInSectionHOptions]
                : titleFeesAsOptions.filter((item) => !titleFeesThatGoInSectionHOptions.includes(item))
            }
            placeholder="Title Fee"
            value={selectedValue}
            formatCreateLabel={noOptionsMessage}
          />
        )}
        <span>to</span>
        <TextInput
          onChange={payeeOnChange}
          placeholder="Payee"
          value={data.payee}
          readOnly={!hasEditPermissions}
          debounceOnValueChange
        />
      </CdLabelCell>
    </SharedItemCells>
  );
};
